import { Analytics } from '@cobliteam/analytics';
import type { VehicleGroup } from '@cobliteam/common-types';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useVehicleGroup from '@cobliteam/hooks/src/useEntity/useVehicleGroup';
import useGlobalFilterStore from '@cobliteam/hooks/src/useGlobalFilterStore';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import { useCallback, useEffect, useMemo, useState } from 'react';
import MultiSelectVehicleGroupsSidebarOverlay from './MultiSelectVehicleGroupsSidebarOverlay';
import MultiSelectVehicleGroupsSidebarTrigger from './MultiSelectVehicleGroupsSidebarTrigger';

export interface MultiSelectVehicleGroupsSidebarProps {
  hideEmptyGroups?: boolean;
  isSidebarOpen?: boolean;
}

const EMPTY_VEHICLE_GROUPS: VehicleGroup[] = [];

const MultiSelectVehicleGroupsSidebar = ({ hideEmptyGroups, isSidebarOpen }: MultiSelectVehicleGroupsSidebarProps) => {
  const [selectedGroups, setSelectedGroups] = useState<VehicleGroup[]>([]);
  const [dropdownState, setDropdownState] = useState({
    isOpen: false,
    forceClosed: false,
    closedByApply: false,
    selectionChanged: false,
  });

  const setVehicleGroupIds = useGlobalFilterStore((state) => state.setVehicleGroupIds);
  const { data: vehicleGroups = EMPTY_VEHICLE_GROUPS, isFetching, isLoading } = useVehicleGroup().useVehicleGroups();
  const { updatePreferences, createPreferences, user, useGetUserData } = useUser();
  const { data: userData } = useGetUserData();
  const userPreferences = userData?.userPreferences;

  const filteredGroups = useMemo(() => (hideEmptyGroups ? vehicleGroups?.filter((group) => Boolean(group.vehicleIds?.length)) : vehicleGroups), [vehicleGroups, hideEmptyGroups]);

  const handleSelectionChange = useCallback(
    (group: VehicleGroup) => {
      setSelectedGroups((prev) => {
        const isSelected = prev.some((g) => g.id === group.id);
        const newSelection = isSelected ? prev.filter((g) => g.id !== group.id) : [...prev, group];

        setDropdownState((prev) => ({ ...prev, selectionChanged: !areSelectionsEqual(newSelection, selectedGroups) }));
        return newSelection;
      });
    },
    [selectedGroups]
  );

  const handleApplyFilter = useCallback(async () => {
    const selectedIds = selectedGroups.map((group) => group.id);
    const preferences = {
      vehicleGroupIds: selectedIds,
      useVehiclesCluster: userPreferences?.useVehiclesCluster ?? false,
    };

    if (userPreferences?.id && user?.id) {
      await updatePreferences.mutate({
        userId: user.id,
        preferencesToUpdate: { ...preferences, id: userPreferences.id },
      });
    } else {
      await createPreferences.mutate({
        userId: user?.id || '',
        preferencesToSave: preferences,
      });
    }

    setVehicleGroupIds(selectedIds);
    setDropdownState((prev) => ({
      ...prev,
      forceClosed: true,
      closedByApply: true,
      selectionChanged: false,
    }));

    Analytics.track('Global Filter Applied');
  }, [selectedGroups, userPreferences, user, updatePreferences, createPreferences, setVehicleGroupIds]);

  const areSelectionsEqual = useCallback(
    (groupsA: VehicleGroup[], groupsB: VehicleGroup[]) => groupsA.length === groupsB.length && groupsA.every((group) => groupsB.some((g) => g.id === group.id)),
    []
  );

  useEffect(() => {
    if (userPreferences?.vehicleGroupIds) {
      const preferencesGroups = vehicleGroups.filter((group) => userPreferences?.vehicleGroupIds?.includes(group.id));
      setSelectedGroups(preferencesGroups);
    }
  }, [userPreferences?.vehicleGroupIds, vehicleGroups]);

  useEffect(() => {
    if (!(dropdownState.isOpen || dropdownState.closedByApply)) {
      const preferencesGroups = vehicleGroups.filter((group) => userPreferences?.vehicleGroupIds?.includes(group.id));
      setSelectedGroups(preferencesGroups);
      setDropdownState((prev) => ({ ...prev, selectionChanged: false }));
    }
    setDropdownState((prev) => ({ ...prev, closedByApply: false }));
  }, [dropdownState.isOpen, dropdownState.closedByApply, userPreferences?.vehicleGroupIds, vehicleGroups]);

  // Memoize dropdown open change handler
  const handleDropdownOpenChange = useCallback(
    () =>
      setDropdownState((prev) => ({
        ...prev,
        forceClosed: false,
        isOpen: !prev.isOpen,
      })),
    []
  );

  // Memoize clear filter handler
  const handleClearFilter = useCallback(() => {
    setSelectedGroups([]);
    setDropdownState((prev) => ({ ...prev, selectionChanged: true }));
  }, []);

  // Memoize cancel handler
  const handleCancel = useCallback(() => {
    setDropdownState((prev) => ({ ...prev, forceClosed: true }));
    const preferencedGroups = vehicleGroups.filter((group) => userPreferences?.vehicleGroupIds?.includes(group.id));
    setSelectedGroups(preferencedGroups);
  }, [vehicleGroups, userPreferences?.vehicleGroupIds]);

  return (
    <div id='sidebar-global-filter-id'>
      <Dropdown
        onOpenChange={handleDropdownOpenChange}
        forceClose={dropdownState.forceClosed}
        placement='bottom-start'
        portal={false}
        overlay={
          <MultiSelectVehicleGroupsSidebarOverlay
            isLoading={isFetching || isLoading}
            vehicleGroups={filteredGroups}
            selectedGroups={selectedGroups}
            selectionChanged={dropdownState.selectionChanged}
            onChange={handleSelectionChange}
            onClearFilter={handleClearFilter}
            onApplyFilter={handleApplyFilter}
            onCancel={handleCancel}
          />
        }
      >
        <div data-testid='multiselect-vehicle-groups-sidebar'>
          <MultiSelectVehicleGroupsSidebarTrigger isOpen={dropdownState.isOpen} totalSelectedGroups={selectedGroups.length} />
        </div>
      </Dropdown>
    </div>
  );
};

MultiSelectVehicleGroupsSidebar.displayName = 'MultiSelectVehicleGroupsSidebar';

export default MultiSelectVehicleGroupsSidebar;
